import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

const Privacy = () => {
  const { t, isHebrew } = useStaticTranslation("privacy");
  const { t: seoT } = useStaticTranslation("seo");

  return (
    <PageTransition>
      <SEO
        title={seoT("privacyTitle", "מדיניות פרטיות")}
        description={seoT("privacyDescription", "מדיניות הפרטיות של אליס תכשיטים - מידע על איסוף נתונים, שימוש בעוגיות, אבטחת מידע והגנה על פרטיות הלקוחות")}
        canonical="https://alice.jewelry/privacy"
        breadcrumbs={[
          { name: seoT("home", "דף הבית"), url: '/' },
          { name: seoT("breadcrumbPrivacy", "מדיניות פרטיות"), url: '/privacy' },
        ]}
      />
      <div className="min-h-screen flex flex-col" dir={isHebrew ? "rtl" : "ltr"}>
        <Header />
        
        <main className="flex-1">
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-12 animate-fade-in">
                <div className="gold-divider mb-6" />
                <h1 className="text-4xl md:text-5xl font-serif mb-4">{t("title", "מדיניות פרטיות")}</h1>
                <p className="text-lg text-muted-foreground">{t("companyName", "תכשיטי אליס בע\"מ")}</p>
              </div>

              <div className={`space-y-6 ${isHebrew ? 'text-right' : 'text-left'} text-sm animate-fade-in`} style={{ animationDelay: '0.1s' }}>
                <p className="text-muted-foreground leading-relaxed">
                  {t("intro", "מדיניות פרטיות זו (להלן: \"מדיניות הפרטיות\") מהווה חלק בלתי נפרד מתנאי השימוש ויש לקוראה יחד עם תנאי השימוש, ולעשות שימוש בהגדרות המופיעות בתנאי השימוש.")}
                </p>

                <p className="text-muted-foreground leading-relaxed">
                  {t("siteDescription", "אתר האינטרנט \"www.alisjewelry.co.il\" (\"האתר\") המופעל ומנוהל על ידי חברת תכשיטי אליס בע\"מ, ח.פ. 514849876 (להלן: \"המפעילה\") משמש בין היתר לסחר מקוון לרכישת מוצרים על ידי הגולשים והמשתמשים באתר (להלן: \"השירות\") וללקוחות הנרשמים והמשתמשים בשירותי המפעילה.")}
                </p>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("generalTitle", "כללי")}</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    {t("generalP1", "כחלק מהשימוש בשירותי האתר, נדרש המשתמש להעביר פרטים אישיים לידי המפעילה ובהם שם, כתובת, טלפון ודוא\"ל, מספר תעודת זהות. פרטים אלה נשמרים על ידי המפעילה במאגר הלקוחות שלה ומשמש לצרכים פנימיים ותקשורת עתידית עם הלקוחות.")}
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("generalP2", "פרטים נוספים, כגון, פרטי כרטיס אשראי נדרשים לצורך חיוב בלבד ואינם נשמרים ע\"י המפעילה.")}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("databaseTitle", "מאגר המידע והשימוש בו")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("databaseDesc", "הנתונים הנאספים אודות המשתמש נשמרים במאגר מידע של המפעילה. המפעילה מתחייבת לעשות שימוש במידע האמור על פי מדיניות פרטיות זו ו/או על פי הוראות כל דין.")}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("cookiesTitle", "שימוש בעוגיות (Cookies)")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("cookiesDesc", "המפעילה עושה שימוש בטכנולוגיות שונות על מנת לאסוף מידע מהמכשיר הנייד ו/או המחשב של המשתמש ואודות פעילות המשתמש באתר. רוב הדפדפנים מקבלים עוגיות באופן אוטומטי, אולם בדרך כלל יכול המשתמש לשנות את הגדרות הדפדפן שלו.")}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("securityTitle", "אבטחת מידע")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("securityDesc", "המפעילה מיישמת באתר מערכות ונהלים עדכניים לאבטחת מידע. מערכות ונהלים אלה מצמצמים את הסיכונים לחדירה בלתי מורשית, אך אין הם מעניקים ביטחון מוחלט.")}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("changesTitle", "שינויים במדיניות הפרטיות")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("changesDesc", "המפעילה רשאית לשנות מעת לעת את הוראות מדיניות פרטיות זו. אם יבוצעו שינויים מהותיים תפורסם על כך הודעה באתר המפעילה.")}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Privacy;
